import React from 'react';
import { ServiceOffering, Language, CommunityStatus } from '@/types';
import { Card, Badge, Button, LocationMapButton } from '@/components/common';
import { getTranslation, getServiceTypesByIds, PAYMENT_PREFERENCES } from '@/constants';
import { getStatusDisplay } from '@/utils';

interface AvailabilitySlot {
  day: string;
  time: string;
}

interface ServiceCardProps {
  service: ServiceOffering;
  language: Language;
  onRequest: (service: ServiceOffering) => void;
  showRequestButton?: boolean;
  isRequested?: boolean;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  language,
  onRequest,
  showRequestButton = true,
  isRequested = false,
}) => {
  const t = getTranslation(language);
  const serviceTypes = getServiceTypesByIds(service.serviceTypes);

  // Parse availability from JSON string
  const parseAvailability = (): AvailabilitySlot[] => {
    if (!service.availability) return [];
    try {
      return JSON.parse(service.availability);
    } catch {
      return [];
    }
  };

  const availability = parseAvailability();

  // Format availability for display
  const formatAvailability = (): string => {
    if (availability.length === 0) return '';

    const dayNames: Record<string, string> = {
      monday: language === 'en' ? 'Mon' : 'Lun',
      tuesday: language === 'en' ? 'Tue' : 'Mar',
      wednesday: language === 'en' ? 'Wed' : 'Mer',
      thursday: language === 'en' ? 'Thu' : 'Jeu',
      friday: language === 'en' ? 'Fri' : 'Ven',
      saturday: language === 'en' ? 'Sat' : 'Sam',
      sunday: language === 'en' ? 'Sun' : 'Dim',
    };

    const timeNames: Record<string, string> = {
      morning: language === 'en' ? 'AM' : 'Matin',
      afternoon: language === 'en' ? 'PM' : 'Après-midi',
      evening: language === 'en' ? 'Eve' : 'Soir',
    };

    // Group by day
    const grouped: Record<string, string[]> = {};
    availability.forEach((slot) => {
      if (!grouped[slot.day]) grouped[slot.day] = [];
      grouped[slot.day].push(timeNames[slot.time] || slot.time);
    });

    return Object.entries(grouped)
      .map(([day, times]) => `${dayNames[day] || day}: ${times.join(', ')}`)
      .join(' | ');
  };

  const getPaymentBadgeColor = (payment: string): 'warning' | 'success' | 'default' => {
    const pref = PAYMENT_PREFERENCES.find((p) => p.key === payment);
    if (!pref) return 'default';

    if (pref.key === 'cash') return 'warning';
    if (pref.key === 'points') return 'success';
    return 'default';
  };

  const getPaymentLabel = (payment: string): string => {
    if (payment === 'cash') return t.cad;
    if (payment === 'points') return t.communityPoints;
    return t.eitherOption;
  };

  return (
    <Card hover className="h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          {service.userPhotoURL ? (
            <img
              src={service.userPhotoURL}
              alt={service.userName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold">
                {service.userName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{service.userName}</h3>
            {service.userCommunityStatus && (
              <div className="text-xs text-gray-600">
                {getStatusDisplay(service.userCommunityStatus as CommunityStatus, language)}
              </div>
            )}
            {service.userTotalExchanges !== undefined && service.userTotalExchanges > 0 && (
              <div className="text-xs text-gray-500">
                {service.userTotalExchanges} {language === 'en' ? 'exchanges' : 'échanges'}
              </div>
            )}
            {service.userPostalCode && (
              <LocationMapButton
                postalCode={service.userPostalCode}
                language={language}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">{t.available}</p>
          <div className="flex flex-wrap gap-1">
            {serviceTypes.map((st) => (
              <span
                key={st.id}
                className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded"
              >
                {st.icon} {st.name[language]}
              </span>
            ))}
          </div>
        </div>

        {service.description && (
          <p className="text-sm text-gray-600">{service.description}</p>
        )}

        <div>
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">
            {t.preferredPayment}
          </p>
          <div className="flex flex-wrap gap-2">
            {service.paymentPreferences.map((payment) => (
              <Badge key={payment} variant={getPaymentBadgeColor(payment)}>
                {PAYMENT_PREFERENCES.find((p) => p.key === payment)?.icon}{' '}
                {getPaymentLabel(payment)}
              </Badge>
            ))}
          </div>
        </div>

        {availability.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {t.availability}
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              {formatAvailability()}
            </p>
          </div>
        )}
      </div>

      {showRequestButton && (
        <div className="mt-4 pt-4 border-t">
          {isRequested ? (
            <Button fullWidth variant="success" disabled>
              ✓ {t.requestSent}
            </Button>
          ) : (
            <Button onClick={() => onRequest(service)} fullWidth variant="primary">
              {t.requestBtn}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
